/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService, type QueryParams } from './base'

export interface ActivityFilter extends QueryParams {
  docuId?: number
  itemId?: string
  type?: string
  pageStart?: number
  pageSize?: number
  sortBy?: string
  sortDesc?: boolean
  action?: string[]
  createdAtFrom?: number
  createdAtTo?: number
  createdById?: number[]
  includeSubFolders?: boolean
  includeSystemGeneratedEvents?: boolean
  itemText?: string
  filterId?: number
}

export interface ActionItem {
  id: string
  value: string
}

export interface ActionsResponse {
  elements?: ActionItem[]
  [key: string]: unknown
}

export interface AuditLogItem {
  id?: number
  createdById?: number
  createdByName?: string
  createdAt?: string | number
  sessionId?: string
  deviceType?: string
  type?: string
  action?: string
  shortInfo?: string
  comment?: string
  auditedId?: number
  itemText?: string
  itemId?: number
  parentId?: number
  parentText?: string
  detail?: string
  esignatureType?: string
  esignatureComment?: string
  documentUId?: number
  lineage?: string
  parseAsArray?: boolean
  multiDataMappingType?: string
  auditType?: string
  auditAction?: string
  auditActionLocalized?: string
  hasEsignature?: boolean
  createdBy?: { id?: number; name?: string }
}

export interface AuditLogsResponse {
  hasNextPage?: boolean
  elements: AuditLogItem[]
  totalElements?: number
  [key: string]: unknown
}

class ActivityApiService {
  async getActions(type?: string | null | undefined): Promise<ActionsResponse> {
    const url = type ? `/api/auditlogs/actions/${type}` : '/api/auditlogs/actions'
    return baseApiService.fetch<ActionsResponse>(url)
  }

  async getClinicalActions(): Promise<ActionsResponse> {
    return baseApiService.fetch<ActionsResponse>('/api/auditlogs/clinicalactions')
  }

  async getUserActions(): Promise<ActionsResponse> {
    return baseApiService.fetch<ActionsResponse>('/api/auditlogs/useractions')
  }

  async getAllAuditLogs(params: ActivityFilter): Promise<AuditLogsResponse> {
    return baseApiService.fetch<AuditLogsResponse>('/api/auditlogs', {
      params
    })
  }

  async getAuditLogsList(params: ActivityFilter): Promise<AuditLogsResponse> {
    return baseApiService.fetch<AuditLogsResponse>('/api/v2/auditlogs', {
      params
    })
  }

  async getAuditLogDetail(id: number): Promise<AuditLogItem> {
    return baseApiService.fetch<AuditLogItem>(`/api/auditlogs/${id}`)
  }

  async getSettingsActions(settingType: string): Promise<ActionsResponse> {
    return baseApiService.fetch<ActionsResponse>(`/api/auditlogs/actions/${settingType}`)
  }

  async getPackageActivityActions(): Promise<ActionsResponse> {
    return baseApiService.fetch<ActionsResponse>('/api/auditlogs/applypackageactions')
  }

  // This api is not used anywhere in the react codebase
  async getUserAuditLogs(userId: number, params: ActivityFilter): Promise<AuditLogsResponse> {
    return baseApiService.fetch<AuditLogsResponse>(`/api/users/${userId}/activities`, {
      params
    })
  }

  async exportAllAuditlogs(params: ActivityFilter): Promise<void> {
    const reqObj = { filter: params }
    return baseApiService.fetch<void>('/api/reports/auditLog', {
      method: 'POST',
      body: reqObj
    })
  }
}

export const activityApiService = new ActivityApiService()
